<script lang="ts" context="module">
    import {
        type CreateDialogProps,
        createDialog as baseCreateDialog,
    } from '@melt-ui/svelte';
    import { escapeCss } from '$lib/utils/escapeCss';

    export function createDialog<TValues, TValue>(
        form: Form<TValues, string>,
        queryField: Field<TValues, TValue>,
        config: CreateDialogProps,
    ) {
        return baseCreateDialog({
            preventScroll: true,
            openFocus: `#${escapeCss(form.getFieldId(queryField))}-mobile`,
            ...config,
        });
    }
</script>

<script lang="ts" generics="TValues, TValue, TItem">
    import type { Field, Form } from '@jmvdevelop/svelte-form';
    import Input from '$components/Form/Input.svelte';
    import { cx } from '$lib/utils/cx';
    import StackZIndex from '$components/ZIndex/StackZIndex.svelte';
    import { fly } from 'svelte/transition';
    import { melt, type ComboboxOptionProps } from '@melt-ui/svelte';
    import { quadOut, quartInOut } from 'svelte/easing';

    export let form: Form<TValues, string>;
    export let queryField: Field<TValues, string | null>;
    export let itemField: Field<TValues, TValue | null>;

    export let label: string = '';
    export let placeholder: string = '';
    export let items: TItem[];
    export let toOption: (item: TItem) => ComboboxOptionProps<TValue>;

    export let dialog: ReturnType<typeof createDialog>;

    // This function is called when items can be loaded. When the component is closed, there is no point in preloading the items.
    // It can be called several times
    export let onItemCanBeLoaded: () => void;

    const {
        elements: { portalled, overlay, content, title, close },
        states: { open },
    } = dialog;

    $: id = `${form.getFieldId(queryField)}-mobile`;
    $: q = form.stores().fieldValue(queryField);

    $: $open ? onItemCanBeLoaded() : undefined;

    function selectItem(item: TItem) {
        const option = toOption(item);
        form.dispatch(form.actions().changeValue(option.label ?? '', queryField));
        form.dispatch(form.actions().changeValue(option.value, itemField));
        $open = false;
    }

    function clear() {
        form.dispatch(form.actions().changeValue('', queryField));
        form.dispatch(form.actions().changeValue(null, itemField));
        const input = document.querySelector(
            `#${escapeCss(id)}`,
        ) as HTMLInputElement | null;
        input?.focus();
    }

    interface $$Slots {
        item: {
            item: TItem;
        };
    }
</script>

<StackZIndex zIndex={1050} let:zIndex>
    <div use:melt={$portalled}>
        {#if $open}
            <div
                use:melt={$overlay}
                class="fixed inset-0 z-[--z] bg-white/20"
                style={`--z: ${zIndex}`}
            />
            <div
                use:melt={$content}
                in:fly={{
                    delay: 0,
                    duration: 300,
                    easing: quadOut,
                    y: -50,
                    opacity: 0,
                }}
                out:fly={{
                    delay: 0,
                    duration: 300,
                    easing: quartInOut,
                    y: -50,
                    opacity: 0,
                }}
                class="absolute top-0 left-0 right-0 h-dvh z-[--z] flex flex-col"
                style={`--z: ${zIndex + 1}`}
            >
                <header
                    class="bg-white w-full rounded-t-[8px] mt-[10px] relative border-b border-0 border-solid border-gray-200"
                >
                    <div use:melt={$title} class="h-[64px] flex items-center">
                        <button
                            type="button"
                            class="btn-reset absolute top-0 hover:opacity-50 w-[44px] h-full text-gray-800 flex items-center justify-center"
                            use:melt={$close}
                            aria-label="Close"
                        >
                            <i class="tnp tnp-cross text-12"></i>
                        </button>
                        <label
                            for={id}
                            class="text-13 font-bold text-gray-800 m-0 px-[20px] text-center w-full"
                            >{label}</label
                        >
                    </div>
                </header>
                <div class="bg-white w-full flex items-center gap-2">
                    <Input
                        {id}
                        defaultClass="border-0 outline-none text-gray-800 placeholder:text-gray-800 placeholder:opacity-50 text-13 w-full pl-[20px] h-[64px]"
                        autofocus={true}
                        {placeholder}
                        {form}
                        field={queryField}
                        toText={(t) => t}
                        fromText={(t) => t}
                        onKeyPress={(event) => {
                            if (event.key === 'Enter') {
                                $open = false;
                            }
                        }}
                    />
                    <div class="pr-[20px]">
                        {#if ($q ?? '').trim() !== ''}
                            <button
                                type="button"
                                aria-label="Clear"
                                on:click|preventDefault={clear}
                                class="btn-reset rounded-full bg-gray-400 hover:opacity-50 size-[20px] text-white flex items-center justify-center"
                            >
                                <i class="tnp tnp-cross text-10" aria-hidden={true} />
                            </button>
                        {/if}
                    </div>
                </div>
                <ul class="m-0 p-0 list-unstyled overflow-y-auto bg-gray-100 h-full">
                    {#each items as item}
                        <li class={cx('relative cursor-pointer scroll-my-2')}>
                            <button
                                class="btn-reset w-full py-[12px] px-[20px] hover:bg-gray-200 text-left"
                                on:click|preventDefault={() => selectItem(item)}
                            >
                                <slot name="item" {item} />
                            </button>
                        </li>
                    {/each}
                </ul>
                <!--<button use:melt={$close}> Close Dialog</button>-->
            </div>
        {/if}
    </div>
</StackZIndex>
